import React, { useState, useEffect } from "react";
import { db } from "../firebase/firebaseConfig";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { Collections } from "../types/type";

// Survey 관련 타입 정의
type SATISFACTION_LEVEL = "HIGH" | "MEDIUM" | "LOW";

interface SurveySubmission {
  id: string;
  userId: string;
  userName: string;
  satisfaction: SATISFACTION_LEVEL;
  firstQuestion: string;
  secondQuestion: string;
  lastQuestion: string;
  timestamp: any; // Firestore Timestamp
  isRewarded: boolean;
}

// UserDeletion 관련 타입 정의
interface UserDeletion {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  question: string;
  response: {
    answer: string;
    question: string;
  };
  timestamp: any; // Firestore Timestamp
}

// 유틸리티 함수
const mapDocToData = (doc: any) => {
  return { id: doc.id, ...doc.data() };
};

// 대시보드 모드 타입
type DashboardMode = "SURVEY" | "DELETION";

// 메인 SurveyDashboard 컴포넌트
const SurveyDashboard: React.FC = () => {
  // 대시보드 모드 상태 추가
  const [mode, setMode] = useState<DashboardMode>("SURVEY");

  const [surveys, setSurveys] = useState<SurveySubmission[]>([]);
  const [deletions, setDeletions] = useState<UserDeletion[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<SATISFACTION_LEVEL | "ALL">(
    "ALL"
  );
  const [selectedSurvey, setSelectedSurvey] = useState<SurveySubmission | null>(
    null
  );
  const [selectedDeletion, setSelectedDeletion] = useState<UserDeletion | null>(
    null
  );
  const [message, setMessage] = useState<string | null>(null);

  // 데이터 가져오기
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const surveyCollection = collection(db, "Survey");
        const surveyQuery = query(
          surveyCollection,
          orderBy("timestamp", "desc")
        );

        const surveySnapshot = await getDocs(surveyQuery);
        const surveyData = surveySnapshot.docs.map((doc) => mapDocToData(doc));

        const transformSurveyData = (item: any) => {
          if (item.responses && item.responses.length > 0) {
            // 응답 배열에서 첫 번째, 두 번째, 세 번째 답변을 추출
            // 순서는 API에서 보내주는 응답 순서와 일치한다고 가정
            const firstQuestion = item.responses[0]?.answer || "";
            const secondQuestion = item.responses[1]?.answer || "";
            const lastQuestion = item.responses[2]?.answer || "";

            return {
              ...item,
              satisfaction:
                item.satisfactionResponse?.level ||
                item.satisfaction ||
                "MEDIUM",
              firstQuestion,
              secondQuestion,
              lastQuestion,
            };
          } else {
            return {
              ...item,
              satisfaction:
                item.satisfaction ||
                item.satisfactionResponse?.level ||
                "MEDIUM",
              firstQuestion: item.firstQuestion || "",
              secondQuestion: item.secondQuestion || "",
              lastQuestion: item.lastQuestion || "",
            };
          }
        };

        const formattedSurveys = surveyData.map((survey) => {
          const transformedSurvey = transformSurveyData(survey);

          return {
            ...transformedSurvey,
            timestamp: transformedSurvey.timestamp?.toDate
              ? transformedSurvey.timestamp.toDate()
              : new Date(),
          };
        });

        setSurveys(formattedSurveys);

        if (formattedSurveys.length > 0) {
          setSelectedSurvey(formattedSurveys[0]);
        }

        const deletionCollection = collection(db, "UserDeletion");
        const deletionQuery = query(
          deletionCollection,
          orderBy("timestamp", "desc")
        );

        const deletionSnapshot = await getDocs(deletionQuery);
        const deletionData = deletionSnapshot.docs.map(
          (doc) => mapDocToData(doc) as UserDeletion
        );

        const formattedDeletions = deletionData.map((deletion) => ({
          ...deletion,
          timestamp: deletion.timestamp?.toDate
            ? deletion.timestamp.toDate()
            : new Date(),
        }));

        setDeletions(formattedDeletions);

        if (formattedDeletions.length > 0) {
          setSelectedDeletion(formattedDeletions[0]);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("데이터를 불러오는 중 오류가 발생했습니다.");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 만족도별 필터링된 설문
  const filteredSurveys =
    selectedTab === "ALL"
      ? surveys
      : surveys.filter((survey) => survey.satisfaction === selectedTab);

  // 특정 설문 선택
  const handleSelectSurvey = (survey: SurveySubmission) => {
    setSelectedSurvey(survey);
  };

  // 특정 삭제 데이터 선택
  const handleSelectDeletion = (deletion: UserDeletion) => {
    setSelectedDeletion(deletion);
  };

  // 스타일 설정
  const styles = {
    container: {
      maxWidth: "100%",
      margin: "0 auto",
      padding: "20px",
      fontFamily: "Arial, sans-serif",
    },
    header: {
      fontSize: "24px",
      fontWeight: "bold",
      marginBottom: "20px",
      color: "#1976d2",
    },
    messageSuccess: {
      backgroundColor: "#e8f5e9",
      color: "#2e7d32",
      padding: "10px",
      borderRadius: "4px",
      marginBottom: "20px",
    },
    messageError: {
      backgroundColor: "#ffebee",
      color: "#c62828",
      padding: "10px",
      borderRadius: "4px",
      marginBottom: "20px",
    },
    mainContent: {
      display: "flex",
      gap: "20px",
    },
    leftPanel: {
      flex: "1",
      maxWidth: window.innerWidth < 768 ? "100%" : "350px",
    },
    rightPanel: {
      flex: "2",
    },
    tabBar: {
      display: "flex",
      backgroundColor: "#f5f5f5",
      borderRadius: "8px 8px 0 0",
      overflow: "hidden",
      marginBottom: "0",
    },
    modeBar: {
      display: "flex",
      backgroundColor: "#e0e0e0",
      borderRadius: "8px",
      overflow: "hidden",
      marginBottom: "20px",
    },
    modeTab: {
      padding: "12px 24px",
      cursor: "pointer",
      flex: "1",
      textAlign: "center" as const,
      fontWeight: "bold",
      fontSize: "16px",
      transition: "background-color 0.3s",
    },
    activeModeTab: {
      backgroundColor: "#1976d2",
      color: "white",
    },
    tab: {
      padding: "12px 16px",
      cursor: "pointer",
      flex: "1",
      textAlign: "center" as const,
      fontWeight: "bold",
      fontSize: "14px",
      transition: "background-color 0.3s",
    },
    activeTab: {
      backgroundColor: "white",
      borderTop: "2px solid #1976d2",
    },
    tabHighlight: {
      color: "#2e7d32",
    },
    tabMedium: {
      color: "#f57c00",
    },
    tabLow: {
      color: "#c62828",
    },
    tabAll: {
      color: "#1976d2",
    },
    tabDeletion: {
      color: "#d32f2f",
    },
    surveyListContainer: {
      backgroundColor: "white",
      border: "1px solid #e0e0e0",
      borderRadius: "0 0 8px 8px",
      maxHeight: "70vh",
      overflowY: "auto" as const,
    },
    surveyCard: {
      padding: "16px",
      borderBottom: "1px solid #e0e0e0",
      cursor: "pointer",
      transition: "background-color 0.3s",
    },
    selectedSurveyCard: {
      backgroundColor: "#f0f7ff",
      borderLeft: "3px solid #1976d2",
    },
    surveyCardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "8px",
    },
    userName: {
      fontWeight: "bold",
      fontSize: "16px",
    },
    date: {
      color: "#757575",
      fontSize: "12px",
    },
    badge: {
      display: "inline-block",
      fontSize: "12px",
      padding: "2px 8px",
      borderRadius: "4px",
      marginLeft: "8px",
    },
    highBadge: {
      backgroundColor: "#e8f5e9",
      color: "#2e7d32",
    },
    mediumBadge: {
      backgroundColor: "#fff3e0",
      color: "#f57c00",
    },
    lowBadge: {
      backgroundColor: "#ffebee",
      color: "#c62828",
    },
    deletionBadge: {
      backgroundColor: "#ffcdd2",
      color: "#d32f2f",
    },
    rewardBadge: {
      backgroundColor: "#e3f2fd",
      color: "#1976d2",
    },
    noSurveys: {
      padding: "30px 20px",
      textAlign: "center" as const,
      color: "#757575",
    },
    detailCard: {
      backgroundColor: "white",
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      padding: "20px",
      height: "70vh",
      overflowY: "auto" as const,
    },
    detailHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "24px",
      borderBottom: "1px solid #e0e0e0",
      paddingBottom: "16px",
    },
    detailTitle: {
      fontSize: "20px",
      fontWeight: "bold",
      marginBottom: "8px",
      display: "flex",
      alignItems: "center",
    },
    detailDate: {
      color: "#757575",
      fontSize: "14px",
      marginBottom: "8px",
    },
    badgeContainer: {
      display: "flex",
      gap: "8px",
    },
    sectionTitle: {
      fontWeight: "bold",
      fontSize: "16px",
      marginBottom: "12px",
    },
    questionBlock: {
      backgroundColor: "#f5f5f5",
      padding: "16px",
      borderRadius: "8px",
      marginBottom: "24px",
      whiteSpace: "pre-wrap" as const,
    },
    questionTitle: {
      fontWeight: "bold",
      marginBottom: "8px",
    },
    questionText: {
      marginBottom: "8px",
    },
    noSelection: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      color: "#757575",
    },
    loadingSpinner: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "200px",
    },
    spinner: {
      width: "40px",
      height: "40px",
      border: "4px solid rgba(0, 0, 0, 0.1)",
      borderLeft: "4px solid #1976d2",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    emailText: {
      color: "#1976d2",
      fontSize: "14px",
      marginBottom: "8px",
    },
  };

  // 만족도 수준에 따른 텍스트와 스타일
  const getSatisfactionInfo = (level: SATISFACTION_LEVEL) => {
    switch (level) {
      case "HIGH":
        return { text: "만족", style: styles.highBadge };
      case "MEDIUM":
        return { text: "보통", style: styles.mediumBadge };
      case "LOW":
        return { text: "불만족", style: styles.lowBadge };
      default:
        return { text: "알 수 없음", style: {} };
    }
  };

  // 날짜 포맷팅
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // 설문 질문 정보 가져오기 (고정값)
  const getQuestionText = (
    questionNumber: number,
    satisfaction: SATISFACTION_LEVEL
  ) => {
    // 실제 프로젝트에서는 만족도 레벨에 따라 다른 질문이 표시될 수 있음
    // 여기서는 간단하게 고정 질문으로 처리
    switch (questionNumber) {
      case 1:
        if (satisfaction === "HIGH") return "어떤 부분이 특히 좋았나요?";
        if (satisfaction === "MEDIUM") return "어떤 부분을 개선하면 좋을까요?";
        if (satisfaction === "LOW") return "어떤 부분이 불만족스러웠나요?";
        return "첫 번째 질문";
      case 2:
        if (satisfaction === "HIGH")
          return "추가적으로 개선하면 좋을 부분은 무엇인가요?";
        if (satisfaction === "MEDIUM") return "어떤 기능이 추가되면 좋을까요?";
        if (satisfaction === "LOW")
          return "가장 시급하게 개선되어야 할 부분은 무엇인가요?";
        return "두 번째 질문";
      case 3:
        return "기타 의견을 자유롭게 남겨주세요.";
      default:
        return `질문 ${questionNumber}`;
    }
  };

  // 설문 대시보드 렌더링
  const renderSurveyDashboard = () => {
    return (
      <div
        style={{
          ...styles.mainContent,
          flexDirection: window.innerWidth < 768 ? "column" : "row",
        }}
      >
        {/* 왼쪽 패널: 설문 목록 */}
        <div style={styles.leftPanel}>
          <div style={styles.tabBar}>
            <div
              style={{
                ...styles.tab,
                ...styles.tabAll,
                ...(selectedTab === "ALL" ? styles.activeTab : {}),
              }}
              onClick={() => setSelectedTab("ALL")}
            >
              전체 ({surveys.length})
            </div>
            <div
              style={{
                ...styles.tab,
                ...styles.tabHighlight,
                ...(selectedTab === "HIGH" ? styles.activeTab : {}),
              }}
              onClick={() => setSelectedTab("HIGH")}
            >
              만족 ({surveys.filter((s) => s.satisfaction === "HIGH").length})
            </div>
            <div
              style={{
                ...styles.tab,
                ...styles.tabMedium,
                ...(selectedTab === "MEDIUM" ? styles.activeTab : {}),
              }}
              onClick={() => setSelectedTab("MEDIUM")}
            >
              보통 ({surveys.filter((s) => s.satisfaction === "MEDIUM").length})
            </div>
            <div
              style={{
                ...styles.tab,
                ...styles.tabLow,
                ...(selectedTab === "LOW" ? styles.activeTab : {}),
              }}
              onClick={() => setSelectedTab("LOW")}
            >
              불만족 ({surveys.filter((s) => s.satisfaction === "LOW").length})
            </div>
          </div>

          <div style={styles.surveyListContainer}>
            {filteredSurveys.length > 0 ? (
              filteredSurveys.map((survey) => {
                const isSelected = selectedSurvey?.id === survey.id;
                const satisfactionInfo = getSatisfactionInfo(
                  survey.satisfaction
                );

                return (
                  <div
                    key={survey.id}
                    style={{
                      ...styles.surveyCard,
                      ...(isSelected ? styles.selectedSurveyCard : {}),
                    }}
                    onClick={() => handleSelectSurvey(survey)}
                  >
                    <div style={styles.surveyCardHeader}>
                      <span style={styles.userName}>
                        {survey.userName || "익명 사용자"}
                      </span>
                      <div>
                        {survey.isRewarded && (
                          <span
                            style={{ ...styles.badge, ...styles.rewardBadge }}
                          >
                            보상 지급
                          </span>
                        )}
                      </div>
                    </div>

                    <div>
                      <span
                        style={{ ...styles.badge, ...satisfactionInfo.style }}
                      >
                        {satisfactionInfo.text}
                      </span>
                      <span style={styles.date}>
                        {formatDate(survey.timestamp)}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={styles.noSurveys}>
                {selectedTab === "ALL"
                  ? "설문 응답이 없습니다."
                  : `${
                      getSatisfactionInfo(selectedTab as SATISFACTION_LEVEL)
                        .text
                    } 응답이 없습니다.`}
              </div>
            )}
          </div>
        </div>

        {/* 오른쪽 패널: 상세 내용 */}
        <div style={styles.rightPanel}>
          {selectedSurvey ? (
            <div style={styles.detailCard}>
              <div style={styles.detailHeader}>
                <div>
                  <div style={styles.detailTitle}>
                    {selectedSurvey.userName || "익명 사용자"}
                    <span
                      style={{
                        ...styles.badge,
                        ...getSatisfactionInfo(selectedSurvey.satisfaction)
                          .style,
                        marginLeft: "10px",
                      }}
                    >
                      {getSatisfactionInfo(selectedSurvey.satisfaction).text}
                    </span>
                  </div>
                  <div style={styles.detailDate}>
                    제출일: {formatDate(selectedSurvey.timestamp)}
                  </div>
                  <div style={styles.detailDate}>
                    사용자 ID: {selectedSurvey.userId || "(없음)"}
                  </div>
                </div>

                <div style={styles.badgeContainer}>
                  {selectedSurvey.isRewarded && (
                    <span style={{ ...styles.badge, ...styles.rewardBadge }}>
                      보상 지급됨
                    </span>
                  )}
                </div>
              </div>

              {/* 첫 번째 질문 응답 */}
              <div style={styles.questionBlock}>
                <div style={styles.questionTitle}>문항 1</div>
                <div style={styles.questionText}>
                  {getQuestionText(1, selectedSurvey.satisfaction)}
                </div>
                <div>
                  {selectedSurvey.firstQuestion || (
                    <span style={{ color: "#9e9e9e" }}>응답 없음</span>
                  )}
                </div>
              </div>

              {/* 두 번째 질문 응답 */}
              <div style={styles.questionBlock}>
                <div style={styles.questionTitle}>문항 2</div>
                <div style={styles.questionText}>
                  {getQuestionText(2, selectedSurvey.satisfaction)}
                </div>
                <div>
                  {selectedSurvey.secondQuestion || (
                    <span style={{ color: "#9e9e9e" }}>응답 없음</span>
                  )}
                </div>
              </div>

              {/* 세 번째 질문 응답 */}
              <div style={styles.questionBlock}>
                <div style={styles.questionTitle}>문항 3</div>
                <div style={styles.questionText}>
                  {getQuestionText(3, selectedSurvey.satisfaction)}
                </div>
                <div>
                  {selectedSurvey.lastQuestion || (
                    <span style={{ color: "#9e9e9e" }}>응답 없음</span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div style={styles.detailCard}>
              <div style={styles.noSelection}>
                <p>왼쪽 목록에서 설문을 선택하세요.</p>
                <p>설문 내용을 확인할 수 있습니다.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // 삭제 설문 대시보드 렌더링
  const renderDeletionDashboard = () => {
    return (
      <div
        style={{
          ...styles.mainContent,
          flexDirection: window.innerWidth < 768 ? "column" : "row",
        }}
      >
        {/* 왼쪽 패널: 삭제 응답 목록 */}
        <div style={styles.leftPanel}>
          <div style={styles.tabBar}>
            <div
              style={{
                ...styles.tab,
                ...styles.tabDeletion,
                ...styles.activeTab,
              }}
            >
              탈퇴 사유 ({deletions.length})
            </div>
          </div>

          <div style={styles.surveyListContainer}>
            {deletions.length > 0 ? (
              deletions.map((deletion) => {
                const isSelected = selectedDeletion?.id === deletion.id;

                return (
                  <div
                    key={deletion.id}
                    style={{
                      ...styles.surveyCard,
                      ...(isSelected ? styles.selectedSurveyCard : {}),
                    }}
                    onClick={() => handleSelectDeletion(deletion)}
                  >
                    <div style={styles.surveyCardHeader}>
                      <span style={styles.userName}>
                        {deletion.userName || "익명 사용자"}
                      </span>
                      <span
                        style={{ ...styles.badge, ...styles.deletionBadge }}
                      >
                        탈퇴
                      </span>
                    </div>

                    <div>
                      <span style={styles.date}>
                        {formatDate(deletion.timestamp)}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={styles.noSurveys}>탈퇴 응답이 없습니다.</div>
            )}
          </div>
        </div>

        {/* 오른쪽 패널: 상세 내용 */}
        <div style={styles.rightPanel}>
          {selectedDeletion ? (
            <div style={styles.detailCard}>
              <div style={styles.detailHeader}>
                <div>
                  <div style={styles.detailTitle}>
                    {selectedDeletion.userName || "익명 사용자"}
                    <span
                      style={{
                        ...styles.badge,
                        ...styles.deletionBadge,
                        marginLeft: "10px",
                      }}
                    >
                      탈퇴
                    </span>
                  </div>
                  <div style={styles.emailText}>
                    이메일: {selectedDeletion.userEmail || "(없음)"}
                  </div>
                  <div style={styles.detailDate}>
                    탈퇴일: {formatDate(selectedDeletion.timestamp)}
                  </div>
                  <div style={styles.detailDate}>
                    사용자 ID: {selectedDeletion.userId || "(없음)"}
                  </div>
                </div>
              </div>

              {/* 삭제 이유 응답 */}
              <div style={styles.questionBlock}>
                <div style={styles.questionTitle}>탈퇴 사유</div>
                <div style={styles.questionText}>
                  {selectedDeletion.question}
                </div>
                <div>
                  {selectedDeletion.response.answer || (
                    <span style={{ color: "#9e9e9e" }}>응답 없음</span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div style={styles.detailCard}>
              <div style={styles.noSelection}>
                <p>왼쪽 목록에서 탈퇴 응답을 선택하세요.</p>
                <p>탈퇴 이유를 확인할 수 있습니다.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.header}>사용자 피드백 대시보드</h1>

      {message && (
        <div style={error ? styles.messageError : styles.messageSuccess}>
          {message}
        </div>
      )}

      {/* 모드 선택 탭 */}
      <div style={styles.modeBar}>
        <div
          style={{
            ...styles.modeTab,
            ...(mode === "SURVEY" ? styles.activeModeTab : {}),
          }}
          onClick={() => setMode("SURVEY")}
        >
          설문 응답 ({surveys.length})
        </div>
        <div
          style={{
            ...styles.modeTab,
            ...(mode === "DELETION" ? styles.activeModeTab : {}),
          }}
          onClick={() => setMode("DELETION")}
        >
          탈퇴 사유 ({deletions.length})
        </div>
      </div>

      {loading ? (
        <div style={styles.loadingSpinner}>
          <div style={styles.spinner}></div>
        </div>
      ) : mode === "SURVEY" ? (
        renderSurveyDashboard()
      ) : (
        renderDeletionDashboard()
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default SurveyDashboard;
